import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

// Imported assets
import { EditIcon, UnhideIcon } from "../../../../assets/icons/svgs/Icons";

// Imported components
import Label from "../../../../components/form/Label";
import PrimaryButton from "../../../../components/ui/PrimaryButton";

// Styled components
const AddOrRemoveContainer = styled.div`
  position: absolute;
  bottom: 32px;
  // right: -32px;
`;

const CancelButton = styled(PrimaryButton)`
  background: white;
  border: 1px solid #d9d9d9;
  color: black;
  cursor: pointer;
  flex-grow: 1;

  &:hover {
    background: lab(from white calc(l - 5) a b / 1);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const CreateNewButton = styled(PrimaryButton)`
  background: white;
  border: 1px solid #d9d9d9;
  color: black;
  display: flex;
  flex-direction: row;
  gap: 8px;
  height: 40px;
  padding: 0 20px;
  width: 150px;

  &:hover {
    background: lab(from white calc(l - 5) a b / 1);
  }
`;

const EditButton = styled(PrimaryButton)`
  display: flex;
  flex-direction: row;
  gap: 8px;
  height: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: auto;
`;

const Modal = styled.div`
  align-items: center;
  background-color: #fff;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  flex-direction: column;
  gap: 8px;
  left: 50%;
  max-height: 80%;
  max-width: 90%;
  min-width: 90%;
  overflow-y: auto;
  padding: 80px;
  position: fixed;
  top: 50%;
  transform: translate(-50%, -50%);
  width: auto;
  z-index: 110;
`;

const Overlay = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 109;
`;

const SaveButton = styled(PrimaryButton)`
  background: var(--terbine-blue);
  cursor: pointer;
  flex-grow: 1;

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 1);
  }
`;

const TemplateButton = styled(PrimaryButton)`
  display: flex;
  direction: row;
  position: absolute;
  right: 0;
  padding: 0 12px;
`;

const ViewPolicyButton = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
`;

// Layout component
const GrayBox = ({ children, sx = {} }) => (
  <Box
    sx={{
      backgroundColor: "#f5f5f5",
      p: 2,
      borderRadius: 2,
      mb: 2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const ViewPolicyModal = ({ data }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(data);

  const [mode, setMode] = useState("View"); // View or Edit

  const resetFormData = useCallback(() => {
    // let loadedData = data;
    // loadedData.startDate = dayjs(data.startDate);
    // loadedData.endDate = dayjs(data.endDate);
    setFormData(data);
  }, [data]);

  useEffect(() => {
    resetFormData();
  }, [data, resetFormData]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleCancel = () => {
    resetFormData();
    setMode("View");
  };

  const handleClose = () => {
    handleCancel();
    toggleModal();
  };

  useEffect(() => {
    const handleUnload = () => {
      setFormData(data);
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [data]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Container>
      <ViewPolicyButton onClick={toggleModal}>
        <UnhideIcon mr="0" />
      </ViewPolicyButton>

      <Overlay $isOpen={isModalOpen} onClick={handleClose} />

      <Modal $isOpen={isModalOpen} id="modal">
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontFamily: "Oxygen, Inter, Roboto, sans-serif" }}
        >
          {mode === "View" ? data.policyName : "Editing Policy"}
        </Typography>

        {mode === "View" && (
          <>
            <Box sx={{ position: "relative", width: "100%" }}>
              <EditButton onClick={() => setMode("Edit")}>
                Edit <EditIcon fill="white" />
              </EditButton>
              <Typography
                variant="body1"
                sx={{ color: "var(--terbine-blue)", mb: 1 }}
              >
                Description
              </Typography>
              <GrayBox>
                <TextField
                  disabled
                  fullWidth
                  multiline
                  rows={8}
                  variant="outlined"
                  value={formData.description}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "32px",
                position: "relative",
                width: "100%",
              }}
            >
              <Box sx={{ position: "relative", width: "100%" }}>
                <Typography
                  variant="body1"
                  sx={{ color: "var(--terbine-blue)", mb: 1 }}
                >
                  Selected Attributes
                </Typography>

                <GrayBox>
                  <TableContainer
                    component={Paper}
                    sx={{
                      borderRadius: "8px",
                      boxShadow: "none",
                      "& *": {
                        fontFamily: "Inter, Oxygen, Roboto, sans-serif",
                      },
                    }}
                  >
                    <Table
                      sx={{
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        "& .MuiTableCell-root": {
                          padding: "8px",
                        }
                      }}
                    >
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor: "var(--terbine-blue)",
                            "& th": {
                              color: "white",
                              fontSize: "clamp(12px, 1.5vw, 18px)",
                              fontWeight: 300,
                              borderBottom: "none",
                            },
                            borderBottom: "none",
                          }}
                        >
                          {[
                            { label: "Attribute", width: "20%" },
                            { label: "Condition", width: "20%" },
                            { label: "Value" },
                          ].map((header, i) => (
                            <TableCell
                              align={header.align || "left"}
                              key={i}
                              width={header.width || null}
                            >
                              {header.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.attributes.map((row, index) =>
                          row.type === "attr" ? (
                            <TableRow
                              key={index}
                              sx={{
                                backgroundColor: "white",
                                borderBottom:
                                  index !== formData.attributes.length - 1
                                    ? "1px solid white"
                                    : "none",
                                borderTop: "none",
                                "& > td": {
                                  borderTop: "none",
                                  borderBottom:
                                    index !== formData.attributes.length - 1
                                      ? "1px solid white"
                                      : "none",
                                  color: "black",
                                },
                              }}
                            >
                              <TableCell>{row.attrType}</TableCell>
                              <TableCell>{row.boolModifier}</TableCell>
                              <TableCell>{row.attrValue}</TableCell>
                            </TableRow>
                          ) : (
                            <TableRow
                              key={index}
                              sx={{
                                backgroundColor: "white",
                                borderBottom:
                                  index !== formData.attributes.length - 1
                                    ? "1px solid white"
                                    : "none",
                                borderTop: "none",
                                "& > td": {
                                  borderTop: "none",
                                  borderBottom:
                                    index !== formData.attributes.length - 1
                                      ? "1px solid white"
                                      : "none",
                                  color: "black",
                                },
                              }}
                            >
                              <TableCell>
                                <span
                                  style={{
                                    background: "var(--terbine-blue)",
                                    borderRadius: "8px",
                                    color: "white",
                                    padding: "8px",
                                    width: "auto",
                                  }}
                                >
                                  {row.conditional}
                                </span>
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </GrayBox>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  position: "relative",
                  width: "100%",
                }}
              >
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ color: "var(--terbine-blue)", mb: 1 }}
                  >
                    Time Period
                  </Typography>
                  <Grid container direction="row" spacing={2} wrap="nowrap">
                    <Grid item xs={3} sm={6}>
                      <Label>Starting Date</Label>
                      <GrayBox>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disabled
                            onChange={(newValue) =>
                              updateField("startDate", newValue)
                            }
                            sx={{ bgcolor: "white", width: "100%" }}
                            timezone="default"
                            // value={formData.startDate}
                          />
                        </LocalizationProvider>
                      </GrayBox>
                    </Grid>

                    <Grid item xs={3} sm={6}>
                      <Label>Ending Date</Label>
                      <GrayBox>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disabled
                            onChange={(newValue) =>
                              updateField("endDate", newValue)
                            }
                            sx={{ bgcolor: "white", width: "100%" }}
                            timezone="default"
                            // value={formData.endDate}
                          />
                        </LocalizationProvider>
                      </GrayBox>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="body1"
                    sx={{ color: "var(--terbine-blue)", mb: 1 }}
                  >
                    Designated Routing
                  </Typography>
                  <Grid container direction="row" spacing={2} wrap="nowrap">
                    <Grid item xs={3} sm={4}>
                      <GrayBox>
                        <TextField
                          disabled
                          select
                          fullWidth
                          variant="outlined"
                          value={formData.designatedRoutingType}
                          onChange={(e) =>
                            updateField("designatedRoutingType", e.target.value)
                          }
                          InputProps={{ sx: { backgroundColor: "white" } }}
                        >
                          {[
                            "Public",
                            "Domain Members",
                            "ISIC Code",
                            "API Key Access",
                          ].map((routingType) => (
                            <MenuItem key={routingType} value={routingType}>
                              {routingType}
                            </MenuItem>
                          ))}
                        </TextField>
                      </GrayBox>
                    </Grid>

                    {formData.designatedRoutingType === "Domain Members" && (
                      <Grid item xs={3} sm={true}>
                        <GrayBox>
                          <TextField
                            disabled
                            fullWidth
                            variant="outlined"
                            value={formData.routingValue}
                            onChange={(e) =>
                              updateField("routingValue", e.target.value)
                            }
                            InputProps={{ sx: { backgroundColor: "white" } }}
                          />
                        </GrayBox>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="body1"
                    sx={{ color: "var(--terbine-blue)", mb: 1 }}
                  >
                    Anonymizer
                  </Typography>

                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled
                          checked={formData.isAnonymized}
                          onChange={(e) =>
                            updateField("isAnonymized", !formData.isAnonymized)
                          }
                        />
                      }
                      label="Enable Anonymizer"
                    />
                  </FormGroup>

                  {formData.isAnonymized && (
                    <>
                      <Label>Custom Text Replacing Anonymized Item</Label>
                      <GrayBox>
                        <TextField
                          disabled
                          fullWidth
                          variant="outlined"
                          value={formData.anonymizerReplacerText}
                          onChange={(e) =>
                            updateField(
                              "anonymizerReplacerText",
                              e.target.value
                            )
                          }
                          InputProps={{ sx: { backgroundColor: "white" } }}
                        />
                      </GrayBox>
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ width: "100%" }}>
              <Typography
                variant="body1"
                sx={{ color: "var(--terbine-blue)", mb: 1 }}
              >
                Note
              </Typography>
              <GrayBox>
                <TextField
                  disabled
                  fullWidth
                  multiline
                  rows={8}
                  variant="outlined"
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>
            </Box>

            <Box sx={{ width: "50%" }}>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <div
                  style={{ display: "flex", flexDirection: "row", gap: "8px" }}
                >
                  <CancelButton onClick={handleClose} type="button">
                    Close
                  </CancelButton>
                </div>
              </div>
            </Box>
          </>
        )}

        {mode === "Edit" && (
          <>
            <Box sx={{ position: "relative", width: "100%" }}>
              {/* <TemplateButton>Choose from Template</TemplateButton> */}
              <Typography
                variant="body1"
                sx={{ color: "var(--terbine-blue)", mb: 1 }}
              >
                Policy Name *
              </Typography>
              <GrayBox>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formData.policyName}
                  onChange={(e) => updateField("policyName", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>
            </Box>

            <Box sx={{ width: "100%" }}>
              <Typography
                variant="body1"
                sx={{ color: "var(--terbine-blue)", mb: 1 }}
              >
                Description *
              </Typography>
              <GrayBox>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  variant="outlined"
                  value={formData.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>
            </Box>

            <Box sx={{ position: "relative", width: "100%" }}>
              <Typography
                variant="body1"
                sx={{ color: "var(--terbine-blue)", mb: 1 }}
              >
                Attributes *
              </Typography>
              {formData.attributes.map((row, i) =>
                row.type === "attr" ? (
                  <>
                    <Grid container direction="row" spacing={2} wrap="nowrap">
                      <Grid item xs={3} sm={3.5}>
                        <GrayBox>
                          <TextField
                            select
                            fullWidth
                            variant="outlined"
                            value={formData.attributes[i].attrType}
                            onChange={(e) => {
                              let attrData = formData.attributes;
                              attrData[i].attrType = e.target.value;
                              updateField("attributes", attrData);
                            }}
                            InputProps={{ sx: { backgroundColor: "white" } }}
                          >
                            {[
                              "Source Name",
                              "Source Type",
                              "Sensor Type",
                              "Manufacturer",
                              "ISIC Code",
                              "City",
                              "County",
                              "State/Province",
                              "Country",
                              "Legal Type",
                              "Regulatory Type",
                              "Standard Type",
                              "Equipment Type",
                              "Accessibility",
                              "Fault Type",
                            ].map((attrType) => (
                              <MenuItem key={attrType} value={attrType}>
                                {attrType}
                              </MenuItem>
                            ))}
                          </TextField>
                        </GrayBox>
                      </Grid>

                      <Grid item xs={3} sm={3}>
                        <GrayBox>
                          <TextField
                            select
                            fullWidth
                            variant="outlined"
                            value={formData.attributes[i].boolModifier}
                            onChange={(e) => {
                              let attrData = formData.attributes;
                              attrData[i].boolModifier = e.target.value;
                              updateField("attributes", attrData);
                            }}
                            InputProps={{ sx: { backgroundColor: "white" } }}
                          >
                            {["IS", "IS NOT"].map((boolModifier) => (
                              <MenuItem key={boolModifier} value={boolModifier}>
                                {boolModifier}
                              </MenuItem>
                            ))}
                          </TextField>
                        </GrayBox>
                      </Grid>

                      <Grid item xs={3} sm={true}>
                        <GrayBox>
                          <TextField
                            fullWidth
                            variant="outlined"
                            value={formData.attributes[i].attrValue}
                            onChange={(e) => {
                              let attrData = formData.attributes;
                              attrData[i].attrValue = e.target.value;
                              updateField("attributes", attrData);
                            }}
                            InputProps={{ sx: { backgroundColor: "white" } }}
                          />
                        </GrayBox>
                      </Grid>
                    </Grid>

                    {i === formData.attributes.length - 1 && (
                      <AddOrRemoveContainer
                        style={{
                          right:
                            formData.attributes.length > 1 ? "-52px" : "-32px",
                        }}
                      >
                        <span
                          style={{ cursor: "pointer", fontSize: "48px" }}
                          onClick={() => {
                            let attrData = formData.attributes;
                            attrData.push({ type: "cond", conditional: "" });
                            attrData.push({
                              type: "attr",
                              attrType: "",
                              boolModifier: "",
                              attrValue: "",
                            });
                            updateField("attributes", attrData);
                          }}
                        >
                          +
                        </span>
                        {formData.attributes.length > 1 && (
                          <span
                            style={{
                              cursor: "pointer",
                              fontSize: "48px",
                              marginLeft: "8px",
                            }}
                            onClick={() => {
                              let attrData = formData.attributes;
                              attrData.pop();
                              attrData.pop();
                              updateField("attributes", attrData);
                            }}
                          >
                            -
                          </span>
                        )}
                      </AddOrRemoveContainer>
                    )}
                  </>
                ) : (
                  <>
                    <Grid container direction="row" spacing={2} wrap="nowrap">
                      <Grid item xs={3} sm={3}>
                        <GrayBox>
                          <TextField
                            select
                            fullWidth
                            variant="outlined"
                            value={formData.attributes[i].conditional}
                            onChange={(e) => {
                              let attrData = formData.attributes;
                              attrData[i].conditional = e.target.value;
                              updateField("attributes", attrData);
                            }}
                            InputProps={{ sx: { backgroundColor: "white" } }}
                          >
                            {["AND", "OR"].map((conditional) => (
                              <MenuItem key={conditional} value={conditional}>
                                {conditional}
                              </MenuItem>
                            ))}
                          </TextField>
                        </GrayBox>
                      </Grid>
                    </Grid>
                  </>
                )
              )}
            </Box>

            <Box sx={{ width: "100%" }}>
              <Typography
                variant="body1"
                sx={{ color: "var(--terbine-blue)", mb: 1 }}
              >
                Designated Routing *
              </Typography>
              <Grid container direction="row" spacing={2} wrap="nowrap">
                <Grid item xs={3} sm={4}>
                  <GrayBox>
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      value={formData.designatedRoutingType}
                      onChange={(e) =>
                        updateField("designatedRoutingType", e.target.value)
                      }
                      InputProps={{ sx: { backgroundColor: "white" } }}
                    >
                      {[
                        "Public",
                        "Domain Members",
                        "ISIC Code",
                        "API Key Access",
                      ].map((routingType) => (
                        <MenuItem key={routingType} value={routingType}>
                          {routingType}
                        </MenuItem>
                      ))}
                    </TextField>
                  </GrayBox>
                </Grid>

                {formData.designatedRoutingType === "Domain Members" && (
                  <Grid item xs={3} sm={true}>
                    <GrayBox>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={formData.routingValue}
                        onChange={(e) =>
                          updateField("routingValue", e.target.value)
                        }
                        InputProps={{ sx: { backgroundColor: "white" } }}
                      />
                    </GrayBox>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Box sx={{ width: "100%" }}>
              <Typography
                variant="body1"
                sx={{ color: "var(--terbine-blue)", mb: 1 }}
              >
                Time Period (optional)
              </Typography>
              <Grid container direction="row" spacing={2} wrap="nowrap">
                <Grid item xs={3} sm={6}>
                  <Label>Starting Date</Label>
                  <GrayBox>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        onChange={(newValue) =>
                          updateField("startDate", newValue)
                        }
                        sx={{ bgcolor: "white", width: "100%" }}
                        timezone="default"
                        // value={formData.startDate}
                      />
                    </LocalizationProvider>
                  </GrayBox>
                </Grid>

                <Grid item xs={3} sm={6}>
                  <Label>Ending Date</Label>
                  <GrayBox>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        onChange={(newValue) =>
                          updateField("endDate", newValue)
                        }
                        sx={{ bgcolor: "white", width: "100%" }}
                        timezone="default"
                        // value={formData.endDate}
                      />
                    </LocalizationProvider>
                  </GrayBox>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ width: "100%" }}>
              <Typography
                variant="body1"
                sx={{ color: "var(--terbine-blue)", mb: 1 }}
              >
                Anonymizer (optional)
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isAnonymized}
                      onChange={(e) =>
                        updateField("isAnonymized", !formData.isAnonymized)
                      }
                    />
                  }
                  label="Enable Anonymizer"
                />
              </FormGroup>

              {formData.isAnonymized && (
                <>
                  <Label>Enter Custom Text to Replace Anonymized Item</Label>
                  <GrayBox>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.anonymizerReplacerText}
                      onChange={(e) =>
                        updateField("anonymizerReplacerText", e.target.value)
                      }
                      InputProps={{ sx: { backgroundColor: "white" } }}
                    />
                  </GrayBox>
                </>
              )}
            </Box>

            <Box sx={{ width: "100%" }}>
              <Typography
                variant="body1"
                sx={{ color: "var(--terbine-blue)", mb: 1 }}
              >
                Add Note (optional)
              </Typography>
              <GrayBox>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  variant="outlined"
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>
            </Box>

            <Box sx={{ width: "50%" }}>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <div
                  style={{ display: "flex", flexDirection: "row", gap: "8px" }}
                >
                  <CancelButton onClick={handleCancel} type="button">
                    Cancel
                  </CancelButton>
                  <SaveButton onClick={toggleModal} type="button">
                    Save
                  </SaveButton>
                </div>
              </div>
            </Box>
          </>
        )}
      </Modal>
    </Container>
  );
};

export default ViewPolicyModal;
